import { useEffect, useState, useCallback } from 'react';
import { isPointInPolygon } from '../utils/geofencing';

type Zone = {
  id: string;
  name: string;
  description: string;
  coordinates: any;
  shape_type: 'circle' | 'polygon';
  latitude: number;
  longitude: number;
  radius?: number;
  playlist_id: string | null;
};

type UserLocation = {
  lat: number;
  lng: number;
} | null;

export function useZoneDetection(zones: Zone[], userLocation: UserLocation) {
  const [currentZone, setCurrentZone] = useState<Zone | null>(null);
  const [previousZone, setPreviousZone] = useState<Zone | null>(null);
  const [justEnteredZone, setJustEnteredZone] = useState(false);

  const checkIfInZone = useCallback((zone: Zone, lat: number, lng: number): boolean => {
    if (zone.shape_type === 'polygon' && zone.coordinates) {
      const coords = typeof zone.coordinates === 'string'
        ? JSON.parse(zone.coordinates)
        : zone.coordinates;

      return isPointInPolygon([lng, lat], coords);
    } else if (zone.shape_type === 'circle' && zone.radius) {
      const R = 6371e3;
      const φ1 = (lat * Math.PI) / 180;
      const φ2 = (zone.latitude * Math.PI) / 180;
      const Δφ = ((zone.latitude - lat) * Math.PI) / 180;
      const Δλ = ((zone.longitude - lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      return distance <= zone.radius;
    }

    return false;
  }, []);

  useEffect(() => {
    if (!userLocation) return;

    const { lat, lng } = userLocation;
    let foundZone: Zone | null = null;

    for (const zone of zones) {
      if (checkIfInZone(zone, lat, lng)) {
        foundZone = zone;
        break;
      }
    }

    if (foundZone && foundZone.id !== currentZone?.id) {
      setPreviousZone(currentZone);
      setCurrentZone(foundZone);
      setJustEnteredZone(true);

      setTimeout(() => {
        setJustEnteredZone(false);
      }, 100);
    } else if (!foundZone && currentZone) {
      setPreviousZone(currentZone);
      setCurrentZone(null);
    }
  }, [userLocation, zones, currentZone, checkIfInZone]);

  return {
    currentZone,
    previousZone,
    justEnteredZone,
    isInZone: currentZone !== null
  };
}
